import React, { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical } from "@fortawesome/pro-regular-svg-icons";
import { StatusCode } from "api/protocols";
import {
  ITeeSheet,
  PutTeeSheet,
} from "api/rpc/2024-04/facilityAdmin/teesheet/teeSheet";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import useModal from "hooks/modals/useModal";
import { formatDate, isEmpty } from "helpers/Helpers";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import { Select } from "components/select/index";
import StatisticsBarBookingSummary, {
  EMPTY_BOOKING_SOURCES,
} from "elements/teesheet/statisticsbar/StatisticsBarBookingSummary";
import StatisticsBarExpandableSearch from "elements/teesheet/statisticsbar/StatisticsBarExpandableSearch";
import StatisticsBarMobileToolbar from "elements/teesheet/statisticsbar/StatisticsBarMobileToolbar";
import StatisticsBarFiltersSkeleton from "elements/teesheet/statisticsbar/StatisticsBarFiltersSkeleton";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import { ITeeTimeNew } from "redux/reducers/models/teetime";
import { ICourse, IDivision } from "redux/reducers/models/facility";
import "elements/teesheet/statisticsbar/statisticsBar.scss";
import OverrideCartsAvailableModal, {
  IOverrideCartsAvailableModalState,
} from "elements/teesheet/statisticsbar/OverrideCartsAvailableModal";
import TeeSheetSideBySideSettingsModal from "components/teeSheetSideBySideSettingsModal/TeeSheetSideBySideSettingsModal";
import {
  ITeeSheetSideBySideSettings,
  IUpdateTeeSheetSideBySideSettings,
} from "hooks/useTeeSheetSideBySideSettings/useTeeSheetSideBySideSettings";
import StatusModal, { IFacilityStatusState } from "elements/teesheet/statisticsbar/StatusModal";
import { PostFacilityStatus, TPostFacilityStatus } from "api/rpc/2024-04/facilityAdmin/facility/status";
import { showError, showSuccess } from "redux/actions/ui";

interface INewStatisticsBarProps {
  teeSheet: ITeeSheet;
  teeTimes: ITeeTimeNew[];
  reloadTeeTimesAndSheet: () => void;
  sideBySideSettings: ITeeSheetSideBySideSettings;
  updateSideBySideSettings: IUpdateTeeSheetSideBySideSettings;
  sideBySideSettingsKeyPrefix: string;
  teeSheetLoaded?: boolean;
  setDebouncedTeeTimeSearch: (teeTimeSearch: string) => void;
  currentCourseId?: number;
  currentDivisionId?: number;
  currentDivisions?: IDivision[];
  onCourseChange: (courseId: number) => void;
  onDivisionChange: (divisionId: number) => void;
  filtersDisabled?: boolean;
  combinedDivisionId: number;
  selectedDate?: Date;
  onDateChange?: (date: Date) => void;
  windowWidth?: number;
}

export default function StarterTeeSheetStatsBars(props: INewStatisticsBarProps) {
  const { t } = useTranslation();
  const { facilityStore, teeSheetStore } = useAppSelector(store => store);
  const dispatch = useAppDispatch();

  const {
    state: overrideCartsAvailableModal,
    updateModal: updateOverrideCartsAvailableModal,
    closeModal: closeOverrideCartsAvailableModal,
  } = useModal<IOverrideCartsAvailableModalState>({
    cartsAvailableInput: "",
  });

  const {
    state: selectSideBySideSettingsModal,
    updateModal: updateSideBySideSettingsModal,
    closeModal: closeSideBySideSettingsModal,
  } = useModal();

  const {
    state: statusModal,
    updateModal: updateStatusModal,
    closeModal: closeStatusModal,
  } = useModal<IFacilityStatusState>({
    date: null,
    inputData: [] as Array<{ status_input_id: number; value: string }>,
  });

  const cartFleetStatisticActive = props.teeSheet && facilityStore.teeSheetSettings?.validate_power_cart_count;

  const cartFleetSize = useMemo(() => {
    if (cartFleetStatisticActive) {
      if (props.teeSheet.carts_available != null) {
        return props.teeSheet.carts_available;
      }

      let updatedCartFleetSize = 0;

      if (facilityStore.teeSheetSettings?.use_cart_inventory) {
        updatedCartFleetSize = props.teeSheet.inventory_carts_available ?? props.teeSheet.default_carts_available ?? 0;
      } else {
        updatedCartFleetSize = props.teeSheet.default_carts_available ?? 0;
      }

      return updatedCartFleetSize;
    } else {
      return 0;
    }
  }, [
    props.teeSheet?.carts_available,
    props.teeSheet?.default_carts_available,
    props.teeSheet?.inventory_carts_available,
    facilityStore.teeSheetSettings?.use_cart_inventory,
  ]);

  const bookedSlotsCount = useMemo(() => {
    let count = 0;

    if (!props.teeTimes) {
      return count;
    }

    props.teeTimes.forEach(teeTime => {
      if (teeTime.slots[0].blocked_type === "open" || teeTime.slots[0].blocked_type === "shotgun") {
        teeTime.slots.forEach(slot => {
          if (slot.tee_time_slot_status === "booked") {
            count += 1;
          }
        });
      }
    });

    return count;
  }, [props.teeTimes]);

  const availableSlotsCount = useMemo(() => {
    let count = 0;

    if (!props.teeTimes) {
      return count;
    }

    props.teeTimes.forEach(teeTime => {
      if (teeTime.slots[0].blocked_type === "open" || teeTime.slots[0].blocked_type === "shotgun") {
        teeTime.slots.forEach(slot => {
          if (slot.tee_time_slot_status === "available") {
            count += 1;
          }
        });
      }
    });

    return count;
  }, [props.teeTimes]);

  const currentNoShows = useMemo(() => {
    const count: string[] = [];

    if (!props.teeTimes) {
      return {
        total: 0,
        no_show_charge: 0,
        no_show_weather: 0,
        no_show_defer: 0,
      };
    }

    props.teeTimes.forEach(teeTime => {
      if (teeTime.slots[0].blocked_type === "open" || teeTime.slots[0].blocked_type === "shotgun") {
        teeTime.slots.forEach(slot => {
          if (slot.customer_id && slot.check_in_status === "no_show") {
            count.push(slot.no_show_type);
          }
        });
      }
    });

    return {
      total: count.length,
      no_show_charge: count.filter(val => val === "charge").length,
      no_show_weather: count.filter(val => val === "weather").length,
      no_show_defer: count.filter(val => val === "defer").length,
    };
  }, [props.teeTimes]);

  const currentBookingSources = useMemo(() => {
    const counts = { ...EMPTY_BOOKING_SOURCES };

    if (!props.teeTimes) {
      return counts;
    }

    props.teeTimes.forEach(teeTime => {
      if (teeTime.slots[0].blocked_type === "open" || teeTime.slots[0].blocked_type === "shotgun") {
        teeTime.slots.forEach(slot => {
          if (slot.tee_time_slot_status !== "booked") {
            return;
          }

          const source = slot.tee_time_bookings_source;
          if (source && source in counts) {
            counts[source as keyof typeof counts] += 1;
          } else if (source) {
            counts.other += 1;
          } else {
            counts.tee_sheet += 1;
          }
        });
      }
    });

    return counts;
  }, [props.teeTimes]);

  async function saveOverrideCartsAvailable() {
    if (!cartFleetStatisticActive) {
      return;
    }

    const updatedCartsAvailable = isEmpty(overrideCartsAvailableModal.cartsAvailableInput)
      ? null
      : Number(overrideCartsAvailableModal.cartsAvailableInput);

    const putTeeSheetRes = await PutTeeSheet(
      {
        id: props.teeSheet?.id,
        carts_available: updatedCartsAvailable,
      },
      true,
    );

    if (putTeeSheetRes.status !== StatusCode.OK) {
      return;
    }

    props.reloadTeeTimesAndSheet();

    updateOverrideCartsAvailableModal({ isOpen: false });
  }

  async function saveFacilityStatus() {
    const { ...rest } = statusModal;
    const params: TPostFacilityStatus = {
      date: formatDate(rest.date),
      input_data: rest.inputData,
    };

    const res = await PostFacilityStatus(params, true);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error saving facility status.")));
      return;
    }

    dispatch(showSuccess(t("Successfully saved the facility status.")));
    closeStatusModal();
  }

  function openOverrideCartsAvailableModal() {
    if (cartFleetStatisticActive) {
      updateOverrideCartsAvailableModal({ isOpen: true });
    }
  }

  function saveSideBySideSettings(updatedSideBySideSettings: ITeeSheetSideBySideSettings) {
    props.updateSideBySideSettings(props.sideBySideSettingsKeyPrefix, updatedSideBySideSettings);
    updateSideBySideSettingsModal({ isOpen: false });
  }

  const isMobile =
    (props.windowWidth ?? 0) <= MOBILE_WIDTH && props.onDateChange != null;

  const toolbarDateFallbackRef = useRef(new Date());
  const toolbarDate = props.selectedDate ?? toolbarDateFallbackRef.current;

  const filtersReady =
    props.currentCourseId != null && props.currentDivisionId != null && props.currentDivisions != null;

  const filterSkeletonCount = useMemo(() => {
    let count = 1;
    const courseCount = facilityStore?.facility?.courses?.length;
    if (courseCount == null || courseCount > 1) {
      count++;
    }
    return count;
  }, [facilityStore?.facility?.courses?.length]);

  const optionsMenu = (
    <NavigationDropdownNew
      rightAlign
      hideChevron
      type="secondary"
      label={<FontAwesomeIcon icon={faEllipsisVertical} />}
      sections={[
        [
          {
            label: t("Edit View"),
            onClick: () => updateSideBySideSettingsModal({ isOpen: true }),
            icon: "table-columns",
            disabled: props.sideBySideSettingsKeyPrefix == null,
            hidden: false,
          },
        ],
        [
          {
            label: t("Daily Status"),
            onClick: () => updateStatusModal({ isOpen: true, date: teeSheetStore.selectedDate as Date }),
            icon: "calendars",
            hidden: false,
          },
        ],
      ]}
    />
  );

  return (
    <div className={classNames("statistics-bar statistics-bar--teesheet", { "statistics-bar--mobile": isMobile })}>
      {isMobile && (
        <StatisticsBarMobileToolbar
          selectedDate={toolbarDate}
          onDateChange={props.onDateChange as (date: Date) => void}
          search={
            <StatisticsBarExpandableSearch
              placeholder={t("Search")}
              setDebouncedTeeTimeSearch={props.setDebouncedTeeTimeSearch}
              mobileOverlay
            />
          }
          menu={optionsMenu}
        />
      )}
      <div className="statistics-bar-booking-content">
        <StatisticsBarBookingSummary
          isLoading={!props.teeTimes}
          bookedCount={bookedSlotsCount}
          availableCount={availableSlotsCount}
          noShows={currentNoShows}
          bookingSources={currentBookingSources}
          cartCount={cartFleetSize}
          onCartClick={cartFleetStatisticActive ? openOverrideCartsAvailableModal : undefined}
          compact={isMobile}
        />
      </div>
      <div className="statistics-bar-action-content">
        <div className="statistics-bar-filters">
          {!isMobile && (
            <StatisticsBarExpandableSearch
              placeholder={t("Search")}
              setDebouncedTeeTimeSearch={props.setDebouncedTeeTimeSearch}
            />
          )}
          {!filtersReady ? (
            <StatisticsBarFiltersSkeleton count={filterSkeletonCount} />
          ) : (
            <>
              {facilityStore?.facility?.courses?.length > 1 && (
                <div className="statistics-bar-filter">
                  <Select
                    placeholder={t("Course")}
                    onChange={(val: number) => props.onCourseChange(val)}
                    defaultValue={props.currentCourseId}
                    disabled={props.filtersDisabled}
                  >
                    {facilityStore.facility.courses.map((course: ICourse, i: number) => (
                      <Select.Option key={i} value={course.id} name={course.long_name}>
                        <span>{course.long_name}</span>
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              )}
              <div className="statistics-bar-filter">
                <Select
                  placeholder={t("Division")}
                  onChange={(val: number) => props.onDivisionChange(val)}
                  defaultValue={props.currentDivisionId}
                  disabled={props.filtersDisabled}
                >
                  {props.currentDivisions.map((division: IDivision, i: number) => (
                    <Select.Option key={i} value={division.id} name={division.title}>
                      <span>{division.title}</span>
                    </Select.Option>
                  ))}
                  <Select.Option value={props.combinedDivisionId} name="Combined">
                    <span>{t("Combined")}</span>
                  </Select.Option>
                </Select>
              </div>
            </>
          )}
        </div>

        {!isMobile && <div className="statistics-bar-action-content-actions">{optionsMenu}</div>}
      </div>

      <OverrideCartsAvailableModal
        cartsAvailable={props.teeSheet?.carts_available}
        state={overrideCartsAvailableModal}
        update={updateOverrideCartsAvailableModal}
        close={closeOverrideCartsAvailableModal}
        onOk={() => void saveOverrideCartsAvailable()}
      />

      <TeeSheetSideBySideSettingsModal
        open={selectSideBySideSettingsModal.isOpen}
        onCancel={closeSideBySideSettingsModal}
        onOk={settings => void saveSideBySideSettings(settings)}
        currentSideBySideSettings={props.sideBySideSettings}
        courses={facilityStore.facility?.courses}
      />

      <StatusModal
        state={statusModal}
        update={updateStatusModal}
        close={closeStatusModal}
        onOk={() => void saveFacilityStatus()}
      />
    </div>
  );
}
